import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAccount } from '@api/auth';
import useQuery from '@hooks/useQuery';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import getErrorMessage from '@utils/errorUtils';
import {
  validatePassword,
  checkMatchPass,
  validateConfirmPassword,
} from './utils';

const CreateAccountPage = () => {
  const query = useQuery();
  const token = query.get('token') as string;
  const navigate = useNavigate();
  const [passInfo, setPassInfo] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [formErrorMessage, setFormErrorMessage] = useState<{
    newPassword: string | undefined;
    confirmPassword: string | undefined;
  }>({
    newPassword: '',
    confirmPassword: '',
  });
  const [verifyStatus, setVerifyStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const [errors, setErrors] = useState<FormError[]>([]);

  const onSubmit = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'newPassword',
      validatePassword(passInfo.newPassword)
    );
    validationErrors = updateErrors(
      validationErrors,
      'confirmPassword',
      validateConfirmPassword(passInfo.confirmPassword)
    );

    if (!validatePassword(passInfo.newPassword) && passInfo.confirmPassword) {
      validationErrors = updateErrors(
        validationErrors,
        'confirmPassword',
        checkMatchPass(passInfo.newPassword, passInfo.confirmPassword)
      );
    }
    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    setVerifyStatus(prev => ({ ...prev, loading: true }));
    try {
      const response: any = await createAccount(
        token,
        passInfo.newPassword.trim()
      );
      if (response && response.status === 'success') {
        localStorage.setItem('token', token);
        navigate('/');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      setVerifyStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });

      validationErrors = updateErrors(
        validationErrors,
        'verifyApi',
        errorMessage
      );
      setErrors(prev => validationErrors);
    } finally {
      setVerifyStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setFormErrorMessage({
      newPassword: getErrorMessageFromErrors(errors, 'newPassword'),
      confirmPassword: getErrorMessageFromErrors(errors, 'confirmPassword'),
    });
  }, [errors]);

  return (
    <div className="govuk-width-container mt-5 mb-5 md:mb-10">
      <h1 className="govuk-heading-l">Confirm your account</h1>

      <div className="govuk-grid-column govuk-!-width-full">
        <p className="govuk-body">
          Your colleague has requested that you create a MyWishes Care account.
          To complete this request and use MyWishes Care you must create a new
          password for your account.
        </p>
        <p className="govuk-body">
          Once you have completed this task you will be redirected to the
          MyWishes Care login page. 
        </p>
        <details className="govuk-details">
          <summary className="govuk-details__summary">
            <span className="govuk-details__summary-text">
              Why have I been asked to create an account?
            </span>
          </summary>
          <div className="govuk-details__text">
            <p className="govuk-body">
              MyWishes Care is being used by an organisation who you work for.
              You have been invited and requested to use MyWishes Care by them.
            </p>
            <p className="govuk-body">
              MyWishes Care is an Advance Care Planning solution for care
              professionals. Your organisation already uses MyWishes Care to
              document &apos;what matters most&apos; for those they provide care
              to.
            </p>
            <p className="govuk-body">
              You have been invited to use MyWishes Care in order to
              &apos;view&apos; existing care plans. If your organisation has
              assigned you additional &apos;write access&apos; you will also be
              able to edit current care plans and create new care plans.
            </p>
          </div>
        </details>
        <h3 className="govuk-heading-s">Your password must have:</h3>

        <ul className="govuk-list govuk-list--bullet">
          <li>at least 8 characters</li>
          <li>at least one lower-case letter</li>
          <li>at least one capital letters</li>
          <li>at least one number</li>
        </ul>

        <form>
          {errors.length > 0 && <NewErrorSummary errors={errors} />}
          <div
            className={`govuk-form-group ${formErrorMessage.newPassword ? 'govuk-form-group--error' : ''}`}
          >
            <label className="govuk-label" htmlFor="newPassword">
              Create a new password
            </label>
            {formErrorMessage.newPassword && (
              <span id="password-error" className="govuk-error-message">
                <span className="govuk-visually-hidden">Error:</span>{' '}
                {formErrorMessage.newPassword}
              </span>
            )}
            <input
              className={`govuk-input govuk-!-width-one-half ${formErrorMessage.newPassword ? 'govuk-input--error' : ''}`}
              id="newPassword"
              name="newPassword"
              type="password"
              value={passInfo.newPassword}
              onChange={e =>
                setPassInfo(prev => ({
                  ...prev,
                  newPassword: e.target.value,
                }))
              }
              aria-describedby={
                formErrorMessage.newPassword ? 'password-error' : undefined
              }
              placeholder="Enter your new password"
            />
          </div>

          <div
            className={`govuk-form-group ${formErrorMessage.confirmPassword ? 'govuk-form-group--error' : ''}`}
          >
            <label className="govuk-label" htmlFor="confirmPassword">
              Confirm your new password
            </label>
            {formErrorMessage.confirmPassword && (
              <span id="verifyPass-error" className="govuk-error-message">
                <span className="govuk-visually-hidden">Error:</span>{' '}
                {formErrorMessage.confirmPassword}
              </span>
            )}
            <input
              className={`govuk-input govuk-!-width-one-half ${formErrorMessage.confirmPassword ? 'govuk-input--error' : ''}`}
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={passInfo.confirmPassword}
              onChange={e =>
                setPassInfo(prev => ({
                  ...prev,
                  confirmPassword: e.target.value,
                }))
              }
              aria-describedby={
                formErrorMessage.confirmPassword
                  ? 'verifyPass-error'
                  : undefined
              }
              placeholder="Reenter your new password"
            />
          </div>

          <button
            type="submit"
            className="govuk-button govuk-!-width-one-half"
            data-module="govuk-button"
            disabled={verifyStatus.loading}
            onClick={onSubmit}
          >
            Confirm Account
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountPage;
